import { WaypointType } from '@treadinc/horizon-api-spec';

import { Job } from '~hooks/useJob/models';
import { LiveSiteData } from '~hooks/useLiveMap/models';
import { Load } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { SiteBasic, WayPoint } from '~hooks/useSites/models';
import {
  isDefaultSiteUsages,
  JobSiteUsage,
  OrderSiteUsage,
} from '~hooks/useSiteUsages/models';

/**
 * Convert a site_usage entry into a LiveSiteData. The `'both'` marker renders
 * a bidirectional pickup/dropoff arrow, so it's only used when the site is
 * used for *both* PICKUP and DROP_OFF. Other multi-usage combinations (e.g.
 * PICKUP + STAGING) fall through to the first usage's type. Returns null if
 * the site is missing coordinates.
 */
export const liveSiteFromSiteUsage = (
  su: JobSiteUsage | OrderSiteUsage,
): LiveSiteData | null => {
  if (!su.site.lat || !su.site.lon) return null;
  const uniqueTypes = new Set(su.usages.map((u) => u.waypointType));
  const isPickupAndDropOff =
    uniqueTypes.has(WaypointType.PICKUP) && uniqueTypes.has(WaypointType.DROP_OFF);
  const waypointType: WaypointType | 'both' = isPickupAndDropOff
    ? 'both'
    : (su.usages[0]?.waypointType ?? WaypointType.PICKUP);
  // We don't split this per-direction — one badge per default site.
  const isDefaultSite = isDefaultSiteUsages(su.usages);
  return new LiveSiteData(
    new SiteBasic(
      su.site.id,
      su.site.name,
      String(su.site.lat ?? ''),
      String(su.site.lon ?? ''),
      su.site.latLon,
      su.site.nextBillionGeofence,
      su.site.fullAddress,
      su.site.discardedAt,
    ),
    waypointType,
    isDefaultSite,
  );
};

/**
 * Merge a waypoint into the `combined` site map. When the existing entry's
 * type and the incoming waypoint's type form a PICKUP+DROP_OFF pair, replace
 * the entry with a fresh LiveSiteData carrying 'both' — never mutate the
 * existing instance, since it may be a shared reference from upstream React
 * state (e.g. liveSiteMarkersById). Other combinations (e.g. PICKUP +
 * STAGING) preserve the existing type, since 'both' specifically denotes
 * bidirectional pickup/dropoff.
 */
export const mergeWaypointIntoCombined = (
  combined: Record<string, LiveSiteData>,
  waypoint: WayPoint | undefined,
): void => {
  if (!waypoint?.site || !waypoint.site.lat || !waypoint.site.lon) return;

  const siteId = waypoint.site.id;
  const existing = combined[siteId];
  const incomingType = waypoint.type as WaypointType;
  if (existing) {
    if (existing.waypointType === 'both') return;
    const completesPickupDropOffPair =
      (existing.waypointType === WaypointType.PICKUP &&
        incomingType === WaypointType.DROP_OFF) ||
      (existing.waypointType === WaypointType.DROP_OFF &&
        incomingType === WaypointType.PICKUP);
    if (completesPickupDropOffPair) {
      combined[siteId] = new LiveSiteData(existing.site, 'both');
    }
    return;
  }

  // Waypoints don't carry default_site, so the waypoint-fallback path can't
  // flag default sites — only the site_usages path (liveSiteFromSiteUsage) can.
  combined[siteId] = new LiveSiteData(waypoint.site, incomingType);
};

interface BuildCombinedSiteMarkersInput {
  /** Live sites from the company-wide subscription. Empty for order/job-specific views. */
  liveSiteMarkersById: Record<string, LiveSiteData>;
  job?: Job;
  /** site_usages for the current job (preferred over `job.waypoints` since waypoints only carry one type per entry). */
  jobSiteUsages?: JobSiteUsage[];
  order?: Order;
  /** site_usages for the current order (preferred over `order.waypoints`). */
  orderSiteUsages?: OrderSiteUsage[];
  loads?: Load[];
  /** Job loaded from Geofence Reports — its waypoints are merged in addition to job/order. */
  geofenceRouteJob?: Job;
}

/**
 * Build the combined map of `site_id → LiveSiteData` rendered by the live map.
 *
 * Sources are merged in priority order so the most authoritative usage info
 * wins:
 *
 *   1. live sites (from the company subscription)
 *   2. job site_usages — fall back to `job.waypoints` if not loaded
 *   3. order site_usages — fall back to `order.waypoints` if not loaded
 *   4. each load's waypoints
 *   5. geofenceRouteJob.waypoints
 *
 * site_usages are preferred over `*.waypoints` because each site_usage entry
 * enumerates all usage types for the site, so multi-usage sites (pickup AND
 * dropoff) collapse to `'both'`. Waypoints carry one type per row, so falling
 * back relies on `mergeWaypointIntoCombined` to upgrade to `'both'` when a
 * second waypoint with a different type lands at the same site.
 */
export const buildCombinedSiteMarkers = ({
  liveSiteMarkersById,
  job,
  jobSiteUsages = [],
  order,
  orderSiteUsages = [],
  loads,
  geofenceRouteJob,
}: BuildCombinedSiteMarkersInput): Record<string, LiveSiteData> => {
  const combined: Record<string, LiveSiteData> = { ...liveSiteMarkersById };

  const addWaypoint = (waypoint: WayPoint | undefined) =>
    mergeWaypointIntoCombined(combined, waypoint);

  // Add job sites — prefer site_usages over job.waypoints.
  if (job && jobSiteUsages.length > 0) {
    for (const su of jobSiteUsages) {
      const siteId = su.site.id;
      if (combined[siteId]) continue;
      const liveSite = liveSiteFromSiteUsage(su);
      if (liveSite) combined[siteId] = liveSite;
    }
  } else {
    job?.waypoints?.forEach(addWaypoint);
  }

  // Add order sites — prefer site_usages over order.waypoints.
  if (order && orderSiteUsages.length > 0) {
    for (const su of orderSiteUsages) {
      const siteId = su.site.id;
      if (combined[siteId]) continue;
      const liveSite = liveSiteFromSiteUsage(su);
      if (liveSite) combined[siteId] = liveSite;
    }
  } else {
    order?.waypoints?.forEach(addWaypoint);
  }

  // Add load sites.
  loads?.forEach((load) => {
    load?.waypoints?.forEach(addWaypoint);
  });

  // Add Geofence Reports job sites.
  geofenceRouteJob?.waypoints?.forEach(addWaypoint);

  return combined;
};
